import React, { useMemo, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@common/components/button';
import { Row, Group, Label } from '@common/components/form';
import { TextInputHookForm } from '@common/components/form/inputs/text';
import { TextareaInputHookForm } from '@common/components/form/inputs/textarea/textarea';
import ModalContent from '@common/components/modal/modal-content';
import ModalWindow from '@common/components/modal/modal-window';
import Stack from '@common/components/stack';

import { CompanyValue } from '@modules/admin/types';

interface CompanyValueFormProps {
  isVisible: boolean;
  onSave: (data: FieldValues) => void;
  editCompanyValue: CompanyValue | null;
  handleClose: () => void;
}

const CompanyValueForm = ({
  isVisible,
  onSave,
  editCompanyValue,
  handleClose,
}: CompanyValueFormProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const defaultValues = useMemo(() => ({
    title: editCompanyValue?.name || '',
    description: editCompanyValue?.description || '',
  }), [editCompanyValue]);

  const {
    control,
    reset,
    handleSubmit,
  } = useForm<FieldValues>({
    defaultValues,
    mode: 'onChange',
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  const onSubmit = async (data: FieldValues) => {
    setLoading(true);
    await onSave(data);
    setLoading(false);
  };

  const modalTitle = editCompanyValue ?
    t('organisation:company_values_modal_title_edit') :
    t('organisation:company_values_modal_title_add');

  const saveButtonText = editCompanyValue ?
    t('organisation:update_company_value_button') :
    t('organisation:add_company_value_button');

  return (
    <ModalWindow
      show={isVisible}
      onHide={handleClose}
    >
      <ModalContent
        title={modalTitle}
        customFooter={(
          <Stack direction="row" gap={2} justify="end">
            <Button onClick={handleClose} disabled={loading}>
              {t('common:carousel_footer_cancel')}
            </Button>
            <Button type="primary" onClick={handleSubmit(onSubmit)} disabled={loading} isLoading={loading}>
              {saveButtonText}
            </Button>
          </Stack>
        )}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Group>
              <Label text={t('organisation:company_value_title_label')} />
              <TextInputHookForm
                name="title"
                control={control}
                rules={{ required: true }}
              />
            </Group>
          </Row>
          <Row>
            <Group>
              <Label text={t('organisation:company_value_description_label')} />
              <TextareaInputHookForm
                name="description"
                control={control}
                minRows={4}
              />
            </Group>
          </Row>
        </form>
      </ModalContent>
    </ModalWindow>
  );
};

export default CompanyValueForm;
